import clsx from 'clsx';
import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import CommonComments from '@/src/components/project/Common/Comments/Comments';
import { FileEntry } from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';
import { Loading } from '@/src/globalComponents';
import { initialize } from '@/src/reducers/project/comments/comments.actions.js';

import { useFileEntryViewerInfoPanel } from '../../../store/fileEntryViewer.infoPanel.store';
import { useFileEntryViewer } from '../../../store/fileEntryViewer.store';

const groupComments = (rawComments) => {
  const comments = {};

  for (const rawComment of rawComments) {
    if (!rawComment.commentGroup) {
      comments[rawComment.id] = rawComment;
    } else {
      if (!comments[rawComment.commentGroup].reply) {
        comments[rawComment.commentGroup].reply = [];
      }

      comments[rawComment.commentGroup].reply.push(rawComment);
    }
  }

  return Object.values(comments);
};

const INBOX_SOURCE = 'inbox';

type CommentsProps = {
  fileEntry: FileEntry;
  projectId: string;
};

const Comments = ({ fileEntry, projectId }: CommentsProps) => {
  const { isFullScreen } = useFileEntryViewer();
  const { infoPanelQueries } = useFileEntryViewerInfoPanel();
  const { useCommentsQuery, onCommentAdded } = infoPanelQueries.comments;
  const commentsQuery = useCommentsQuery();
  const { isTrash } = useInboxSearchParams();
  const dispatch = useDispatch();

  const initializeCommentsData = useCallback(
    (comments) => {
      const document = {
        id: fileEntry.file.id,
        linkId: fileEntry.file.id,
        isActive: true,
        isDeleted: isTrash ? true : fileEntry.isDeleted || false,

        origin: fileEntry.file.rawName,
      };

      dispatch(
        initialize({
          projectId,
          document,
          list: comments,
          onAddComment: onCommentAdded,
          source: INBOX_SOURCE,
        })
      );
    },
    [dispatch, fileEntry, onCommentAdded, projectId, isTrash]
  );

  useEffect(() => {
    if (!commentsQuery.data) {
      return;
    }

    const rawComments = commentsQuery.data.data.map((c) => ({
      ...c,
      isActive: true,
    }));

    const groupedComments = groupComments(rawComments);

    initializeCommentsData(groupedComments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentsQuery.data]);

  if (commentsQuery.isLoading || !commentsQuery.data) {
    return <Loading />;
  }

  return (
    <div
      className={clsx('wrapper--comments', 'tw-relative', 'tw-px-[20px] tw-pb-[15px] tw-h-full')}
    >
      {/* <p className={clsx('tw-mb-[10px]', 'tw-text-cfo-black tw-font-brandonTextMedium')}>
        Comments {comments.length > 0 ? `(${comments.length})` : ''}
      </p> */}
      <div className={`tw-h-full fileViewerContents ${!isFullScreen ? 'closed' : ''}`}>
        <CommonComments
          projectId={projectId}
          isFullScreen={false}
          textEditorClasses={clsx(
            // (!) is not removed here as it is to override the styles from legacy code that hasn't been refactored yet
            'tw-static',
            'tw-bg-white tw-shadow-[0px_0px_1px_#eeeeee]',
            'tw-border-solid tw-border-[1px] tw-border-cfo-grey-500 tw-rounded-[10px]'
          )}
        />
      </div>
    </div>
  );
};

export default Comments;
